import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const Dashboard = ({
  assessments,
  onViewDetails,
  onCreateNew,
  onLogout,
  userRoles,
  currentUser, // Prop to get current user's details
}) => {
  const [filteredAssessments, setFilteredAssessments] = useState([]); // This will hold the final, displayed list
  const [clientFilter, setClientFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const navigate = useNavigate();

  // --- Role-based access control checks ---
  const isClient = Array.isArray(userRoles) && userRoles.includes("Client");
  const isManagerialRole =
    Array.isArray(userRoles) &&
    (userRoles.includes("Manager") || userRoles.includes("AssisstantManager"));
  const isTester = Array.isArray(userRoles) && userRoles.includes("Tester");
  // ✅ ADDED: Check if the current user is a TeamLead
  const isTeamLead = Array.isArray(userRoles) && userRoles.includes("TeamLead");

  // ✅ UPDATED: Pre-filter assessments based on the user's role before applying other filters.
  const visibleAssessments = useMemo(() => {
    // Filter for Testers
    if (isTester && currentUser?.name) {
      return assessments.filter((assessment) =>
        assessment.versions?.some(
          (version) =>
            version.preparedBy && version.preparedBy.includes(currentUser.name)
        )
      );
    }

    // ✅ ADDED: Filter for TeamLeads
    if (isTeamLead && currentUser?.name) {
      return assessments.filter((assessment) =>
        // An assessment is visible if it has at least one version (`some`)
        // where the `reviewedBy` field matches the current TeamLead's name.
        assessment.versions?.some(
          (version) => version.reviewedBy === currentUser.name
        )
      );
    }

    // For all other roles (Manager, Client, etc.), show all assessments.
    return assessments;
  }, [assessments, isTester, isTeamLead, currentUser]);

  useEffect(() => {
    let currentAssessments = [...visibleAssessments];

    if (clientFilter) {
      currentAssessments = currentAssessments.filter(
        (a) => a.clientName === clientFilter
      );
    }
    if (statusFilter) {
      currentAssessments = currentAssessments.filter(
        (a) => a.status?.name === statusFilter
      );
    }
    if (dateFilter) {
      currentAssessments = currentAssessments.filter(
        (a) => a.assessmentStartDate === dateFilter
      );
    }

    if (sortConfig.key) {
      currentAssessments.sort((a, b) => {
        const aValue =
          sortConfig.key === "status" ? a.status?.name : a[sortConfig.key];
        const bValue =
          sortConfig.key === "status" ? b.status?.name : b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredAssessments(currentAssessments);
    setCurrentPage(1);
  }, [visibleAssessments, clientFilter, statusFilter, dateFilter, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const totalPages = Math.ceil(filteredAssessments.length / itemsPerPage);
  const currentItems = filteredAssessments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="container card">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <img src="/varutra-logo.png" height={50} alt="Varutra Logo" />
          <h1 className="text-3xl font-bold text-gray-800">
            Assessment Dashboard
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          {isClient && currentUser?.clientLogoUrl ? (
            <img
              src={currentUser.clientLogoUrl}
              height={50}
              alt={`${currentUser.name || "Client"} Logo`}
              className="max-h-12"
            />
          ) : (
            <button onClick={onLogout} className="btn-primary">
              Logout
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label htmlFor="clientName" className="form-label">
            Client Name
          </label>
          <select
            id="clientName"
            className="form-select"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <option value="">All Clients</option>
            {[...new Set(visibleAssessments.map((a) => a.clientName))].map(
              (client, index) => (
                <option key={client || `client-${index}`} value={client}>
                  {client}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label htmlFor="status" className="form-label">
            Status
          </label>
          <select
            id="status"
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {[
              ...new Set(
                visibleAssessments.map((a) => a.status?.name).filter(Boolean)
              ),
            ].map((status, index) => (
              <option key={status || `status-${index}`} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="creationDate" className="form-label">
            Creation Date
          </label>
          <input
            type="date"
            id="creationDate"
            className="form-input"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg shadow-md mb-6">
        <table className="min-w-full bg-white rounded-lg">
          <thead>
            <tr className="table-header">
              <th onClick={() => requestSort("clientName")}>Client Name</th>
              <th onClick={() => requestSort("activityName")}>
                Assessment Name
              </th>
              <th onClick={() => requestSort("assessmentStartDate")}>
                Creation Date
              </th>
              <th onClick={() => requestSort("status")}>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((assessment, index) => {
                const isButtonDisabled =
                  !isManagerialRole &&
                  isClient &&
                  assessment.status?.name !== "L2 Approved" &&
                  assessment.status?.name !== "Closed";

                return (
                  <tr
                    key={
                      assessment.assessmentId ||
                      `${assessment.clientName}-${index}`
                    }
                    className="table-row"
                  >
                    <td>{assessment.clientName}</td>
                    <td>{assessment.activityName}</td>
                    <td>{assessment.assessmentStartDate}</td>
                    <td>{assessment.status?.name}</td>
                    <td>
                      <button
                        onClick={() => onViewDetails(assessment)}
                        className="btn-primary"
                        disabled={isButtonDisabled}
                        title={
                          isButtonDisabled
                            ? "You can only view assessments that are L2 Approved or Closed."
                            : "View assessment details"
                        }
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="5"
                  style={{ textAlign: "center", padding: "1rem" }}
                >
                  No assessments found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center my-4 space-x-2">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1 ? "bg-gray-300" : "bg-blue-500 text-white"
            }`}
          >
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 rounded ${
                currentPage === i + 1
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages
                ? "bg-gray-300"
                : "bg-blue-500 text-white"
            }`}
          >
            Next
          </button>
        </div>
      )}

      <div className="flex justify-end">
        <button
          id="createNewAssessmentBtn"
          onClick={() => {
            navigate("/assessment/new");
          }}
          // ✅ ADDED: Also disable for TeamLeads
          disabled={isClient || isTester || isTeamLead}
          className="btn-primary"
          title={
            isClient || isTester || isTeamLead
              ? "You do not have permission to create new assessments."
              : "Create a new assessment"
          }
        >
          Create New Assessment
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
